/**
 * apiClient.ts - wrapper fetch partage par tous les *Api.ts du portail.
 *
 * Apporte 2 fonctionnalites :
 *  1. Gestion centralisee du 401 -> dispatch event 'nis365:session-expired'
 *     que AuthProvider ecoute pour clear user + AuthGate redirect vers /login
 *  2. Parsing JSON uniforme + propagation d erreurs typees
 *
 * Usage dans un wrapper :
 *   import { apiFetch } from './apiClient';
 *   export const myApi = {
 *     getThings: () => apiFetch<Thing[]>('/things'),
 *   };
 */
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';

export class ApiError extends Error {
  status:    number;
  code?:     string;
  details?:  unknown;
  constructor(status: number, message: string, code?: string, details?: unknown) {
    super(message);
    this.status  = status;
    this.code    = code;
    this.details = details;
  }
}

interface ApiEnvelope<T> {
  data?:  T;
  error?: { code?: string; message?: string; details?: unknown };
}

/**
 * Dispatch globalement quand une requete renvoie 401.
 * AuthProvider l ecoute via window.addEventListener('nis365:session-expired')
 * pour clear le user. AuthGate redirige ensuite vers /login.
 */
function signalSessionExpired(reason: string) {
  window.dispatchEvent(new CustomEvent('nis365:session-expired', { detail: { reason } }));
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
  });

  // Special : 401 = session expiree, on signal et on throw
  if (res.status === 401) {
    signalSessionExpired(`401 sur ${init.method ?? 'GET'} ${path}`);
    throw new ApiError(401, 'Session expirée — reconnexion requise', 'unauthorized');
  }

  // Tente de parse le JSON enveloppe
  const json = (await res.json().catch(() => ({}))) as ApiEnvelope<T>;

  if (!res.ok) {
    throw new ApiError(
      res.status,
      json.error?.message ?? `HTTP ${res.status}`,
      json.error?.code,
      json.error?.details,
    );
  }

  return (json.data ?? (json as unknown as T)) as T;
}
